import { BoxV2 as Box, CustomCheckbox } from 'portal-commons';
import {
  FunctionComponent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TableCell, TableRow } from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';

import CarrierTerms from '../CarrierTerms';
import { getCampaignOperations } from '../../apis';
import { CampaignOperation, PartnerCampaign } from '../../types';
import { remapTCRStatus } from '../../utils';
import { sortMnoList } from '../../../../../utils';
import { convertTimeWithTimezone } from '../../../../../utils/time';

interface AcceptedCampaignListingRowProps {
  selectable?: boolean;
  selected?: boolean;
  error?: boolean;
  campaign: PartnerCampaign;
  onChange?: (selected: boolean) => void;
}

const AcceptedCnpCampaignListingRow: FunctionComponent<
  AcceptedCampaignListingRowProps
> = ({
  selectable = false,
  selected = false,
  error = false,
  campaign,
  onChange,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [height, setHeight] = useState(0);
  const [mnoInfo, setMnoInfo] = useState<CampaignOperation[]>([]);
  const [showMnoInfo, setShowMnoInfo] = useState(false);
  const carrierTermRef = useRef<HTMLDivElement>(null);

  const additionalCellStyle = useMemo(
    () =>
      height > 0
        ? { paddingTop: '12px' }
        : { height: '38px', paddingTop: 0, paddingBottom: 0 },
    [height]
  );

  useEffect(() => {
    if (showMnoInfo && carrierTermRef.current) {
      setHeight(carrierTermRef.current.clientHeight + 38);
    }
  }, [showMnoInfo]);

  const getMnoInfo = async () => {
    if (mnoInfo.length) {
      setShowMnoInfo(true);
    } else {
      const operations = await getCampaignOperations(campaign.uid);
      if (operations.length > 0) {
        setMnoInfo(operations);
        setShowMnoInfo(true);
      }
    }
  };

  const handleToggleCarrierTerm = (event: MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    const nextVisibility = !showMnoInfo;
    if (nextVisibility) {
      getMnoInfo();
    } else {
      setShowMnoInfo(false);
      setHeight(0);
    }
  };

  return (
    <TableRow
      style={{
        position: 'relative',
        height: mnoInfo ? `${height}px` : '38px',
        cursor: 'pointer',
        ...(error ? { background: '#F5DDE2' } : {}),
        ...(height ? { verticalAlign: 'top' } : {}),
      }}
      onClick={() =>
        history.push({
          pathname: `/cnp-campaigns/${campaign.uid}`,
          state: {
            goBackPage: `${location.pathname}${
              location.search ? location.search : ''
            }`,
          },
        })
      }
    >
      {selectable && (
        <TableCell
          align="left"
          style={{ width: 40, ...additionalCellStyle }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <CustomCheckbox
            checked={selected}
            onChange={(event) => {
              onChange?.(event.target.checked);
            }}
          />
        </TableCell>
      )}
      <TableCell align="left" style={{ ...additionalCellStyle }}>
        {campaign.uid}
      </TableCell>
      <TableCell align="left" style={{ ...additionalCellStyle }}>
        {campaign.brandUid}
      </TableCell>
      <TableCell
        align="left"
        style={{
          maxWidth: '150px',
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...additionalCellStyle,
        }}
      >
        <span title={campaign.usecase}>{campaign.usecase}</span>
      </TableCell>
      <TableCell
        align="left"
        style={{
          maxWidth: '150px',
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...additionalCellStyle,
        }}
      >
        <span title={campaign.downstreamCnpName}>
          {campaign.downstreamCnpName}
        </span>
      </TableCell>
      <TableCell align="left" style={{ ...additionalCellStyle }}>
        {campaign.sharedOnDate
          ? convertTimeWithTimezone(
              campaign.sharedOnDate,
              undefined,
              'MM/DD/YYYY'
            )
          : ''}
      </TableCell>
      <TableCell
        align="left"
        style={{
          maxWidth: '150px',
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...additionalCellStyle,
        }}
      >
        <span title={campaign.upstreamCnpName}>{campaign.upstreamCnpName}</span>
      </TableCell>
      <TableCell align="left" style={{ width: 145, ...additionalCellStyle }}>
        {campaign.sharedToDate
          ? convertTimeWithTimezone(
              campaign.sharedToDate,
              undefined,
              'MM/DD/YYYY'
            )
          : ''}
      </TableCell>
      <TableCell align="left" style={{ width: 92, ...additionalCellStyle }}>
        <Box
          as="span"
          sx={{
            position: 'relative',
            left: '10px',
            cursor: 'pointer',
            ...(height ? { top: '-5px', color: '#D41C54' } : {}),
            '&:hover': {
              color: '#D41C54',
            },
          }}
        >
          <MoreHorizIcon onClick={handleToggleCarrierTerm} />
        </Box>
        {showMnoInfo && !!mnoInfo.length && (
          <CarrierTerms
            mnoList={sortMnoList(mnoInfo, ['rulesEngineResultsJson', 'mno'])}
            ref={carrierTermRef}
          />
        )}
      </TableCell>
      <TableCell
        align="left"
        style={{
          width: 85,
          maxWidth: '150px',
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...additionalCellStyle,
        }}
      >
        <span title={remapTCRStatus(campaign.status)}>
          {remapTCRStatus(campaign.status)}
        </span>
      </TableCell>
      <TableCell align="left" style={{ ...additionalCellStyle }}>
        {campaign.sharedToStatus}
      </TableCell>
    </TableRow>
  );
};

export default AcceptedCnpCampaignListingRow;
